import { SharingTypes, UpdateSharingTypes } from '@/app/models/projectSubTypes';
import { useUpdateSubtypesMutation } from '@/app/services/projectSubtypeApi';
import StandardDialog from '@/components/common/dialogs/StandardDialog';
import {
  Box,
  Button,
  Checkbox,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Toolbar,
  Typography,
} from '@mui/material';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

const RowTableCell = styled(TableCell)(() => ({
  fontSize: 13,
  backgroundColor: 'white',
  borderBottom: 'none',
}));

const Separator = styled('div')({
  height: '1px',
  backgroundColor: '#999999',
  width: '100%',
  margin: 0,
  padding: 0,
});

type EnableSharingTypeTableProps = {
  rows: SharingTypes[];
};

export default function EnableSharingTypeTable(props: EnableSharingTypeTableProps) {
  const { rows } = props;
  const { t } = useTranslation(['enableSharingType', 'common']);
  const [openProcessDialog, setOpenProcessDialog] = useState(false);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [selected, setSelected] = useState<UpdateSharingTypes[]>(
    rows
      .filter((row) => row.enabled)
      .map((row) => ({
        code: row.code,
        enabled: row.enabled,
      })),
  );
  const [updateSubtypes] = useUpdateSubtypesMutation();

  React.useEffect(() => {
    if (rows && rows.length > 0) {
      setSelected(
        rows
          .filter((row) => row.enabled)
          .map((row) => ({
            code: row.code,
            enabled: row.enabled,
          })),
      );
    }
  }, [rows]);

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;

    setSelected(
      rows.map((item) => ({
        code: item.code,
        enabled: isChecked,
      })),
    );
  };

  const handleClick = (code: string, checked: boolean) => {
    setSelected((prevSelected) => {
      const itemIndex = prevSelected.findIndex((item) => item.code === code);
      if (itemIndex !== -1) {
        return prevSelected.map((item) => (item.code === code ? { ...item, enabled: !item.enabled } : item));
      }
      if (checked) {
        const selectedItem = rows.find((row) => row.code === code);
        if (selectedItem) {
          return [...prevSelected, { code: selectedItem.code, enabled: true }];
        }
      }

      return prevSelected;
    });
  };

  console.log(selected);

  const handleProcess = async () => {
    try {
      await updateSubtypes({ subTypes: selected });
      toast.success(
        t('processed.successfully', {
          position: toast.POSITION.TOP_RIGHT,
        }),
      );
    } catch (error) {
      console.error('Erro na requisição:', error);
      toast.error(
        t('processed.error', {
          position: toast.POSITION.TOP_RIGHT,
        }),
      );
    }
  };

  const equal = rows.every((row) => {
    const included = selected.some((item) => item.code === row.code && item.enabled);
    if (row.enabled) {
      return included;
    }
    return !included;
  });

  return (
    <Box sx={{ width: '100%' }}>
      <Toolbar style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
        <Typography style={{ marginLeft: '-1rem' }} variant="h6" component="div">
          <Checkbox
            indeterminate={selected.length > 0 && selected.length < rows.length}
            checked={selected.length === rows.length}
            onChange={handleSelectAllClick}
            style={{ color: selected.length > 0 ? '#ff7b00' : 'inherit' }}
          />
          {t('common:sharing.type')}
        </Typography>
        <Button
          disabled={equal}
          variant="primary"
          color="primary"
          onClick={() => {
            setOpenProcessDialog(true);
          }}
        >
          {t('common:save')}
        </Button>
        <StandardDialog
          title={t('confirm.adjustment')}
          contentText={t('common:confirm.process')}
          open={openProcessDialog}
          onClose={() => {
            setButtonDisable(false);
            setOpenProcessDialog(false);
          }}
          onConfirm={() => {
            setOpenProcessDialog(false);
            if (!buttonDisable) {
              setButtonDisable(true);
            }
            handleProcess();
          }}
          cancelText={t('common:no')}
          confirmText={t('common:yes')}
        />
      </Toolbar>
      <Separator />
      <TableContainer>
        <Table sx={{ minWidth: 400 }} aria-label="simple table">
          <TableBody>
            {rows.map((row) => {
              const isItemSelected = selected.some((item) => item.code === row.code);

              return (
                <TableRow
                  hover
                  key={row.code}
                  role="checkbox"
                  aria-checked={isItemSelected}
                  selected={isItemSelected}
                  sx={{ cursor: 'pointer' }}
                >
                  <RowTableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      checked={selected.some((item) => item.code === row.code && item.enabled)}
                      onChange={(event) => {
                        handleClick(row.code, event.target.checked);
                      }}
                      style={{
                        color: selected.some((item) => item.code === row.code && item.enabled) ? '#ff7b00' : 'inherit',
                      }}
                    />
                  </RowTableCell>
                  <RowTableCell>{row.name}</RowTableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
